import { useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { Gauge, MessageSquare, Plus } from 'lucide-react'
import { cn } from '@/lib/utils'
import { CaptureSheet } from './CaptureSheet'

/** Bottom tab bar shared by /cockpit and /concierge on small screens.
 * Route-driven (unlike ZinsserMobileNav's store-driven views) — the two
 * surfaces are separate routes. Owns the CaptureSheet open state so each
 * page only has to render <CockpitTabBar />. */
export function CockpitTabBar({ onCaptureCreated }: { onCaptureCreated?: () => void }) {
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const [captureOpen, setCaptureOpen] = useState(false)

  const tabs = [
    { label: 'Cockpit', icon: Gauge, path: '/cockpit' },
    { label: 'Chat', icon: MessageSquare, path: '/concierge' },
  ]

  return (
    <>
      <nav className="fixed bottom-0 left-0 right-0 z-40 bg-background/95 backdrop-blur-sm border-t border-border md:hidden">
        <div className="flex items-center justify-around h-14 pb-safe">
          {tabs.slice(0, 1).map(renderTab)}
          <button
            type="button"
            onClick={() => setCaptureOpen(true)}
            aria-label="Quick capture"
            className="flex items-center justify-center w-11 h-11 border border-border text-foreground active:bg-muted/50"
          >
            <Plus size={20} strokeWidth={1.5} />
          </button>
          {tabs.slice(1).map(renderTab)}
        </div>
      </nav>
      <CaptureSheet
        open={captureOpen}
        onClose={() => setCaptureOpen(false)}
        onCreated={onCaptureCreated}
      />
    </>
  )

  function renderTab(tab: (typeof tabs)[number]) {
    const Icon = tab.icon
    const isActive = pathname.startsWith(tab.path)
    return (
      <button
        key={tab.path}
        type="button"
        onClick={() => navigate(tab.path)}
        className={cn(
          'relative flex flex-col items-center justify-center flex-1 h-full gap-1 transition-colors',
          'active:bg-muted/50 min-w-[64px]',
          isActive ? 'text-foreground' : 'text-muted-foreground'
        )}
      >
        <Icon className="w-5 h-5" strokeWidth={isActive ? 2 : 1.5} />
        <span className={cn('text-[10px] leading-none', isActive && 'font-medium')}>{tab.label}</span>
        {isActive && <div className="absolute bottom-1.5 w-8 h-0.5 bg-foreground rounded-full" />}
      </button>
    )
  }
}
